import React, { useState, useEffect } from "react";
import {
	Button,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Autocomplete,
} from "@mui/material";
import Datagrid from "../../components/Datagrid/Datagrid";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import customHttp from "../../services/customHTTP";
import { useSite } from "../../context/siteContext";
import Swal from "sweetalert2";
import { AddCircle } from "@mui/icons-material";

const ManageDocuments = () => {
	const [documents, setDocuments] = useState([]);
	const [categories, setCategories] = useState([]);
	const [machines, setMachines] = useState([]);
	const [newDoc, setNewDoc] = useState({
		name: "",
		category: "",
		description: "",
		file: null,
	});
	const [isNewCategory, setIsNewCategory] = useState(false);
	const [newCategory, setNewCategory] = useState("");
	const [selectedMachines, setSelectedMachines] = useState([]);
	const [openDialog, setOpenDialog] = useState(false);
	const [error, setError] = useState({});
	const { selectedSite, siteData } = useSite();

	const columns = [
		{ field: "name", headerName: "Name", width: 300 },
		{ field: "category", headerName: "Category", width: 300 },
		{ field: "description", headerName: "Description", flex: 1 },
		{
			field: "actions",
			headerName: "Actions",
			width: 150,
			renderCell: (params) => (
				<IconButton color="error" onClick={() => handleDeleteDocument(params.row.id)}>
					<DeleteIcon />
				</IconButton>
			),
		},
	];

	const customButtons = [
		{
			label: "Add Document",
			icon: <AddCircle />,
			onClick: () => {
				setSelectedMachines([]);
				setOpenDialog(true);
			},
		},
	];

	useEffect(() => {
		const fetchDocuments = async () => {
			try {
				const site_id = siteData.find((site) => site.customer_site_number === selectedSite)?.site_id;
				const docRes = await customHttp.post("/documents/getDocumentsMetaData", { site_id });
				setDocuments(docRes.data);
			} catch (err) {
				console.error("Error fetching data", err);
			}
		};

		const fetchCategories = async () => {
			try {
				const site_id = siteData.find((site) => site.customer_site_number === selectedSite)?.site_id;
				const catRes = await customHttp.post("/documents/getCategories", { site_id });
				const responseCategories = catRes.data.map((item) => item.category);
				setCategories(responseCategories);
				if (responseCategories.length > 0) {
					setNewDoc({ ...newDoc, category: responseCategories[0] });
				} else {
					setNewDoc({ ...newDoc, category: "new" });
					setIsNewCategory(true);
				}
			} catch (err) {
				console.error("Error fetching categories", err);
			}
		};

		const fetchMachines = async () => {
			try {
				const respMachines = await customHttp.post("/machines/bysiteid", { site_number: selectedSite });
				const allMachines = respMachines.data.data;
				console.log(allMachines);
				setMachines(allMachines);
			} catch (error) {
				console.log(error);
			}
		};
		fetchMachines();
		fetchDocuments();
		fetchCategories();
	}, [selectedSite]);

	const handleFileChange = (e) => {
		console.log(e.target.files[0]);
		setNewDoc({
			...newDoc,
			name: e.target.files[0].name.split(".").slice(0, -1).join("."),
			file: e.target.files[0],
		});
	};

	const handleAddDocument = async () => {
		setError({});
		const newErrors = {};
		const site_id = siteData.filter((i) => i.customer_site_number === selectedSite)[0]?.site_id;
		if (!newDoc.name) newErrors["name"] = "Name is required";
		if (newDoc.category === "") newErrors["category"] = "Category is required";
		if (newDoc.category === "new" && !newCategory) newErrors["category"] = "Category is required";
		if (!newDoc.file) newErrors["file"] = "File is required";
		setError((prevErrors) => ({ ...prevErrors, ...newErrors }));
		if (Object.keys(newErrors).length > 0) {
			return;
		}

		const formData = new FormData();
		formData.append("name", newDoc.name);
		formData.append("description", newDoc.description);
		formData.append("category", isNewCategory ? newCategory : newDoc.category);
		formData.append("file", newDoc.file);
		formData.append("site_id", site_id);
		if (selectedMachines) {
			const selectedMachineIds = selectedMachines.map((machine) => {
				return machine.machine_id;
			});
			console.log(selectedMachineIds);
			formData.append("assigned_machines", selectedMachineIds);
		}

		try {
			const resp = await customHttp.post("/documents/uploadDocument", formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			const updatedDocuments = await customHttp.post("/documents/getDocumentsMetaData", { site_id });
			setDocuments(updatedDocuments.data);

			setOpenDialog(false);
			setNewDoc({
				name: "",
				category: "",
				description: "",
				file: null,
			});
			setIsNewCategory(false);
			setSelectedMachines([]);
			Swal.fire({
				icon: "success",
				title: "success",
				text: "Document uploaded successfully",
			});
		} catch (err) {
			if (err.response && err.response.status === 501) {
				// setOpenDialog(false);
				setError((prevErrors) => ({
					...prevErrors,
					name: "Duplicate document name",
				}));
				setError({ name: "Duplicate document name" });
			} else {
				setOpenDialog(false);
				setNewDoc({
					name: "",
					category: "",
					description: "",
					file: null,
				});
				setIsNewCategory(false);
				setSelectedMachines([]);
				Swal.fire({
					icon: "error",
					title: "Error",
					text: "Something went wrong. Please try again",
				});
				console.error(err);
			}
		}
	};

	const handleDeleteDocument = async (id) => {
		try {
			const confirmation = await Swal.fire({
				title: "Are you sure?",
				text: "This will delete the document permanently.",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#d33",
				cancelButtonColor: "#3085d6",
				confirmButtonText: "Yes, delete it!",
			});

			if (!confirmation.isConfirmed) return;

			await customHttp.delete(`/documents/deleteDocument/${id}`);

			setDocuments((prevDocuments) => prevDocuments.filter((document) => document.id !== id));

			Swal.fire({
				icon: "success",
				title: "Deleted",
				text: "Document deleted successfully",
			});
		} catch (err) {
			console.error("Error deleting document:", err);
			Swal.fire({
				icon: "error",
				title: "Error",
				text: "Could not delete the document. Please try again.",
			});
		}
	};

	const handleCategoryChange = (e) => {
		setNewDoc({ ...newDoc, category: e.target.value });
	};

	const handleNewCategoryChange = (e) => {
		setNewCategory(e.target.value);
	};

	const handleCloseModal = () => {
		setNewDoc({
			name: "",
			category: "",
			description: "",
			file: null,
		});
		setIsNewCategory(false);
		setSelectedMachines([]);
		setOpenDialog(false);
	};

	return (
		<div>
			<Datagrid data={documents} columns={columns} rowIdField="id" customButtons={customButtons} />

			<Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Add New Document</DialogTitle>
				<DialogContent
					style={{
						overflowY: "auto",
					}}>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							marginBottom: "20px",
							marginTop: "10px",
						}}>
						<TextField
							label="Document Name"
							value={newDoc.name}
							onChange={(e) => setNewDoc({ ...newDoc, name: e.target.value })}
							size="small"
							fullWidth
							style={{ marginRight: "10px" }}
							error={!!error.name}
							helperText={error.name}
						/>

						<IconButton component="label" color={error.file ? "error" : "primary"}>
							<CloudUploadIcon />
							<input type="file" hidden onChange={handleFileChange} />
						</IconButton>
					</div>

					<TextField
						label="Document Description"
						value={newDoc.description}
						onChange={(e) => setNewDoc({ ...newDoc, description: e.target.value })}
						size="small"
						fullWidth
						style={{ marginBottom: "20px" }}
					/>

					<FormControl
						fullWidth
						style={{
							marginBottom: "20px",
							minWidth: 200,
						}}>
						<InputLabel id="doc-cat" size="small">
							Category
						</InputLabel>
						<Select
							value={newDoc.category}
							onChange={handleCategoryChange}
							error={!!error.category}
							helperText={error.category}
							label="Category"
							labelId="doc-cat"
							size="small">
							{categories.map((category) => (
								<MenuItem key={category} value={category}>
									{category}
								</MenuItem>
							))}
							<MenuItem value="new" onClick={() => setIsNewCategory(true)}>
								+ Create New Category
							</MenuItem>
						</Select>
					</FormControl>

					{isNewCategory && (
						<div
							style={{
								height: "56px",
								marginBottom: "20px",
							}}>
							<TextField
								label="New Category"
								value={newCategory}
								size="small"
								onChange={handleNewCategoryChange}
								error={!!error.category}
								helperText={error.category}
								fullWidth
							/>
						</div>
					)}
					<Autocomplete
						size="small"
						multiple
						options={machines}
						getOptionLabel={(machine) => `${machine.machine_number} - ${machine.machine_name}`}
						value={selectedMachines}
						onChange={(event, newValue) => {
							console.log(newValue);
							setSelectedMachines(newValue);
						}}
						renderInput={(params) => (
							<TextField {...params} label="Assign to Machines" variant="outlined" />
						)}
					/>
				</DialogContent>

				<DialogActions>
					<Button onClick={handleCloseModal} color="secondary">
						Cancel
					</Button>
					<Button onClick={handleAddDocument} color="primary">
						Add Document
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default ManageDocuments;
